import { Menu, Transition, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment, useState } from 'react';
import { Duration } from 'date-fns';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import DateRangePopup from '~/shared/components/DateRangePopup';
import CalendarIcon from '~/shared/assets/icons/calendar.svg';
import { getDateFilterDates } from '~/shared/utils/getDateFilterDates';
import Typography from '~/shared/components/Typography';

import { formatDate } from '../utils/formatNumber';

import { CUSTOM_DATES_TITLE } from './dateFilterMenuItems';

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

function ChartFilterDropdown<
  T extends { title: string; duration?: { start: Duration; end?: Duration } },
>({
  items,
  onSelect,
  selectedItem,
  align = 'top-right',
}: {
  onSelect: (selection: T) => void;
  items?: T[];
  selectedItem: T;
  align?: 'top-right' | 'top-left';
}) {
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const isCustomDateSelected = selectedItem.title?.includes(CUSTOM_DATES_TITLE);
  const [startDate, endDate] = getDateFilterDates(selectedItem?.duration, selectedItem.title);

  const handleCustomDateSelect = (startDate: Date, endDate: Date) => {
    const start = startDate.toISOString();
    const end = endDate.toISOString();
    const customItem = {
      title: `${CUSTOM_DATES_TITLE}|${start}|${end}`,
    };

    onSelect(customItem as T);
    setIsCustomDateOpen(false);
  };

  return (
    <div className="relative">
      <Menu as="div" className="relative flex">
        {({ open }) => (
          <>
            <MenuButton
              className={clsx(
                'bg-white text-primary-900 border  border-gray-300 flex justify-between w-60 2xl:w-[290px] 2xl:min-w-[290px] rounded-[8px] px-2 py-3 h-9 2xl:h-11 cursor-pointer',
                open
                  ? 'bg-primary-20  border-primary-200'
                  : 'hover:border-primary-300  active:border-primary-100'
              )}
              onClick={() => {
                if (isCustomDateOpen) {
                  setIsCustomDateOpen(false);
                }
              }}
            >
              <div className="flex items-center justify-between w-full">
                <Typography variant="p3-reg" color="text-gray-700">
                  {isCustomDateSelected
                    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                    : selectedItem?.title}
                </Typography>

                <ChevronDownIcon
                  className={clsx(
                    'w-[14px] h-[14px] text-primary-900 stroke-3 cursor-pointer',
                    open ? 'rotate-180' : ''
                  )}
                />
              </div>
            </MenuButton>

            <Transition as={Fragment} {...transitionProps}>
              <MenuItems
                className={clsx(
                  'absolute right-0 top-[100%] rounded-lg mt-2.5 origin-top-right w-60 2xl:w-[290px] bg-white',
                  'shadow-filter-dropdown  focus-outline-none border-primary-70',
                  'border border-primary-70 focus:outline-hidden z-30',
                  align === 'top-right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'
                )}
              >
                <div className="p3">
                  {items?.map((item) => (
                    <MenuItem
                      as={'button'}
                      key={item.title}
                      className={clsx(
                        'text-sm leading-6 w-full h-12 2xl:h-14 flex items-center justify-between px-5 py-[18px] border-gray-200 last:rounded-b-lg cursor-pointer hover:bg-primary-20 group',
                        items?.[0].title === item.title && 'rounded-t-lg',
                        (item.title === selectedItem?.title ||
                          (item.title === CUSTOM_DATES_TITLE && isCustomDateSelected)) &&
                          'bg-primary-20 border border-primary-500',
                        !(
                          item.title === selectedItem?.title ||
                          (item.title === CUSTOM_DATES_TITLE && isCustomDateSelected)
                        ) &&
                          item.title !== items?.[items.length - 1]?.title &&
                          'border-b border-gray-200'
                      )}
                      onClick={() => {
                        if (item.title === CUSTOM_DATES_TITLE) {
                          setIsCustomDateOpen(true);
                        } else {
                          onSelect?.(item);
                        }
                      }}
                    >
                      <Typography color="text-primary-900" variant="p3-reg">
                        {item.title}
                      </Typography>
                      {item.title === 'Custom dates' && (
                        <CalendarIcon
                          width="24px"
                          className={clsx(
                            item.title === selectedItem?.title
                              ? 'stroke-[#2D2D32]'
                              : 'stroke-[#2D2D32]',
                            'size-6 group-hover:stroke-[#2D2D32] transition-colors duration-200'
                          )}
                        />
                      )}
                    </MenuItem>
                  ))}
                </div>
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>

      {isCustomDateOpen && (
        <div
          className={clsx(
            'absolute z-30 top-[100%] mt-1 rounded-md w-fit sm:px-0',
            align === 'top-right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'
          )}
        >
          <DateRangePopup
            startDateStr={startDate}
            endDateStr={endDate}
            onSubmit={handleCustomDateSelect}
            onClose={() => setIsCustomDateOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

export default ChartFilterDropdown;
